import {
  Camera,
  GitCompareArrows,
  MapPinPlus,
  Orbit,
  Move,
  Rotate3d,
  Ruler,
  Scissors,
  Thermometer,
  Trash2,
} from 'lucide-react';
import type { Measurement, ViewerTool } from '../engine/types';
import { FloatingPanel, StageButton } from './FloatingPanel';

/**
 * The tools, bottom-left as in the workbench.
 *
 * Two kinds sit here. The first group changes what a left-click does: rotate,
 * pan, drop a note, measure. Exactly one is on. The second group are things
 * laid over the model or run on it - the section, the maps, the margin walk,
 * the comparison with the last version - which toggle independently, because
 * a dentist checking fit wants the section and a map at once.
 */
interface ToolDockProps {
  tool: ViewerTool;
  onTool: (tool: ViewerTool) => void;
  sectionOn: boolean;
  onSection: () => void;
  heatmapOn: boolean;
  onHeatmap: () => void;
  /** The version to compare against, when there is one. */
  previousVersion: number | null;
  compareOn: boolean;
  onCompare: () => void;
  walkingMargin: boolean;
  onMarginWalk: () => void;
  onScreenshot: () => void;
  measurements: Measurement[];
  onClearMeasurements: () => void;
  /** Hides note pinning, for a decided review where notes are closed. */
  canPin?: boolean;
}

export function ToolDock({
  tool,
  onTool,
  sectionOn,
  onSection,
  heatmapOn,
  onHeatmap,
  previousVersion,
  compareOn,
  onCompare,
  walkingMargin,
  onMarginWalk,
  onScreenshot,
  measurements,
  onClearMeasurements,
  canPin = true,
}: ToolDockProps) {
  return (
    <div className="flex flex-col items-start gap-2">
      {measurements.length > 0 && (
        <FloatingPanel className="min-w-40 px-3 py-2">
          <div className="mb-1 flex items-center justify-between gap-3">
            <span className="text-[11px] font-medium tracking-wide text-neutral-500 uppercase">
              Measurements
            </span>
            <button
              type="button"
              onClick={onClearMeasurements}
              aria-label="Clear measurements"
              className="rounded p-0.5 text-neutral-400 hover:text-neutral-800 dark:hover:text-white"
            >
              <Trash2 className="size-3.5" />
            </button>
          </div>
          <ol className="space-y-0.5">
            {measurements.map((measurement, index) => (
              <li
                key={measurement.id}
                className="flex justify-between gap-4 font-mono text-[12px] tabular-nums"
              >
                <span className="text-neutral-500">#{index + 1}</span>
                <span className="font-semibold text-neutral-800 dark:text-neutral-100">
                  {measurement.millimetres.toFixed(2)} mm
                </span>
              </li>
            ))}
          </ol>
        </FloatingPanel>
      )}

      {tool === 'measure' && (
        <FloatingPanel className="px-3 py-1.5 text-[12px] text-neutral-600 dark:text-neutral-300">
          Click two points on the model. <kbd className="font-mono text-[11px]">Esc</kbd> cancels.
        </FloatingPanel>
      )}
      {tool === 'pin' && (
        <FloatingPanel className="px-3 py-1.5 text-[12px] text-neutral-600 dark:text-neutral-300">
          Click the spot your note is about.
        </FloatingPanel>
      )}

      <FloatingPanel
        role="toolbar"
        aria-label="Viewer tools"
        className="flex items-center gap-0.5 p-1"
      >
        <StageButton
          label="Rotate"
          shortcut="R"
          active={tool === 'rotate'}
          onClick={() => {
            onTool('rotate');
          }}
        >
          <Rotate3d />
        </StageButton>
        <StageButton
          label="Pan"
          shortcut="P"
          active={tool === 'pan'}
          onClick={() => {
            onTool('pan');
          }}
        >
          <Move />
        </StageButton>
        {canPin && (
          <StageButton
            label="Pin a note"
            shortcut="N"
            active={tool === 'pin'}
            onClick={() => {
              onTool('pin');
            }}
          >
            <MapPinPlus />
          </StageButton>
        )}
        <StageButton
          label="Measure"
          shortcut="M"
          active={tool === 'measure'}
          onClick={() => {
            onTool('measure');
          }}
        >
          <Ruler />
        </StageButton>

        <span className="mx-1 h-6 w-px bg-neutral-900/10 dark:bg-white/15" />

        <StageButton label="Section" shortcut="S" active={sectionOn} onClick={onSection}>
          <Scissors />
        </StageButton>
        <StageButton label="Heatmap" shortcut="H" active={heatmapOn} onClick={onHeatmap}>
          <Thermometer />
        </StageButton>
        <StageButton
          label="Walk the margin"
          shortcut="W"
          active={walkingMargin}
          onClick={onMarginWalk}
        >
          <Orbit />
        </StageButton>
        {previousVersion !== null && (
          <StageButton
            label={`Compare with v${String(previousVersion)}`}
            shortcut="C"
            active={compareOn}
            onClick={onCompare}
          >
            <GitCompareArrows />
          </StageButton>
        )}
        <StageButton label="Screenshot" onClick={onScreenshot}>
          <Camera />
        </StageButton>
      </FloatingPanel>
    </div>
  );
}
